import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { applyTheme, getTheme } from "./lib/theme";
import { TabsProvider } from "./lib/tabs";
import { canAccess } from "./lib/pages";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import LRs from "./pages/LRs";
import Invoices from "./pages/Invoices";
import Quotations from "./pages/Quotations";
import Leads from "./pages/Leads";
import PersonalAccounts from "./pages/PersonalAccounts";
import Payments from "./pages/Payments";
import LiveTracking from "./pages/LiveTracking";
import DriverCheckin from "./pages/DriverCheckin";
import Bids from "./pages/Bids";
import Reports from "./pages/Reports";
import Expenses from "./pages/Expenses";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Masters from "./pages/Masters";
import Aging from "./pages/Aging";
import MobilePOD from "./pages/MobilePOD";
import PublicPOD from "./pages/PublicPOD";
import PublicTrack from "./pages/PublicTrack";
import PublicLiveShare from "./pages/PublicLiveShare";
import PublicDriver from "./pages/PublicDriver";
import PublicExpense from "./pages/PublicExpense";
import BankStatements from "./pages/BankStatements";
import LRDetail from "./pages/LRDetail";
import CustomerWork from "./pages/CustomerWork";
import EntityWork from "./pages/EntityWork";
import Summary from "./pages/Summary";
import StaffWork from "./pages/StaffWork";
import Assistant from "./pages/Assistant";
import Inbox from "./pages/Inbox";
import Reminders from "./pages/Reminders";
import Consignments from "./pages/Consignments";
import Payroll from "./pages/Payroll";
import Handling from "./pages/Handling";
import Layout from "./components/Layout";
import MarketingHome from "./marketing/MarketingHome";
import ServicePage from "./marketing/ServicePage";
import FleetIndex from "./marketing/FleetIndex";
import FleetPage from "./marketing/FleetPage";
import AboutPage from "./marketing/AboutPage";
import DefencePage from "./marketing/DefencePage";
import ContactPage from "./marketing/ContactPage";
import BlogIndex from "./marketing/BlogIndex";
import BlogPost from "./marketing/BlogPost";
import GuidePage from "./marketing/GuidePage";
import CityIndex from "./marketing/CityIndex";
import CityPage from "./marketing/CityPage";
import ServicesIndex from "./marketing/ServicesIndex";
import { Toaster } from "sonner";

function Protected({ children, adminOnly = false, page = "" }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#68665E] text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/app" replace />;
  if (page && !canAccess(user, page)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ---------- Public marketing site ---------- */}
      <Route path="/" element={<MarketingHome />} />
      <Route path="/services/:slug" element={<ServicePage />} />
      <Route path="/services" element={<ServicesIndex />} />
      <Route path="/fleet" element={<FleetIndex />} />
      <Route path="/fleet/:slug" element={<FleetPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/defence" element={<DefencePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/insights" element={<BlogIndex />} />
      <Route path="/insights/:slug" element={<BlogPost />} />
      <Route path="/guides/:slug" element={<GuidePage />} />
      <Route path="/air-suspension-trailer-transporter" element={<CityIndex verticalKey="air-suspension" />} />
      <Route path="/air-suspension-trailer-transporter/:city" element={<CityPage verticalKey="air-suspension" />} />
      <Route path="/low-bed-trailer-transporter" element={<CityIndex verticalKey="low-bed" />} />
      <Route path="/low-bed-trailer-transporter/:city" element={<CityPage verticalKey="low-bed" />} />
      <Route path="/odc-heavy-haul-transporter" element={<CityIndex verticalKey="odc-heavy-haul" />} />
      <Route path="/odc-heavy-haul-transporter/:city" element={<CityPage verticalKey="odc-heavy-haul" />} />

      <Route path="/login" element={<Login />} />
      <Route path="/p/pod/:token" element={<PublicPOD />} />
      <Route path="/p/track/:token" element={<PublicTrack />} />
      <Route path="/p/live/:token" element={<PublicLiveShare />} />
      <Route path="/p/driver/:token" element={<PublicDriver />} />
      <Route path="/p/expense/:token" element={<PublicExpense />} />

      {/* ---------- Internal application ---------- */}
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/lrs" element={<Protected page="lrs"><LRs /></Protected>} />
      <Route path="/lr/:id" element={<Protected page="lrs"><LRDetail /></Protected>} />
      <Route path="/customer/:name" element={<Protected><CustomerWork /></Protected>} />
      <Route path="/entity/:etype/:key" element={<Protected><EntityWork /></Protected>} />
      <Route path="/summary" element={<Protected page="summary"><Summary /></Protected>} />
      <Route path="/staff/:id" element={<Protected adminOnly><StaffWork /></Protected>} />
      <Route path="/handling/:id" element={<Protected page="lrs"><Handling /></Protected>} />
      <Route path="/invoices" element={<Protected page="invoices"><Invoices /></Protected>} />
      <Route path="/quotations" element={<Protected page="quotations"><Quotations /></Protected>} />
      <Route path="/leads" element={<Protected page="leads"><Leads /></Protected>} />
      <Route path="/personal-accounts" element={<Protected page="personal_accounts"><PersonalAccounts /></Protected>} />
      <Route path="/payments" element={<Protected page="payments"><Payments /></Protected>} />
      <Route path="/live-tracking" element={<Protected page="live_tracking"><LiveTracking /></Protected>} />
      <Route path="/track-driver/:token" element={<DriverCheckin />} />
      <Route path="/bids" element={<Protected page="bids"><Bids /></Protected>} />
      <Route path="/expenses" element={<Protected page="expenses"><Expenses /></Protected>} />
      <Route path="/masters" element={<Protected page="masters"><Masters /></Protected>} />
      <Route path="/aging" element={<Protected page="aging"><Aging /></Protected>} />
      <Route path="/reports" element={<Protected page="reports"><Reports /></Protected>} />
      <Route path="/assistant" element={<Protected page="assistant"><Assistant /></Protected>} />
      <Route path="/inbox" element={<Protected page="inbox"><Inbox /></Protected>} />
      <Route path="/reminders" element={<Protected page="reminders"><Reminders /></Protected>} />
      <Route path="/board" element={<Navigate to="/consignments" replace />} />
      <Route path="/consignments" element={<Protected page="consignments"><Consignments /></Protected>} />
      <Route path="/payroll" element={<Protected page="payroll"><Payroll /></Protected>} />
      <Route path="/bank-statements" element={<Protected page="bank-statements"><BankStatements /></Protected>} />
      <Route path="/field" element={<Protected page="field"><MobilePOD /></Protected>} />
      <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
      <Route path="/settings" element={<Protected adminOnly><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Scope the high-tech dark cockpit to the internal app; keep the public
// marketing/tracking pages on their light editorial theme.
function ThemeManager() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    const isPublic = pathname === "/" || pathname.startsWith("/services") ||
      pathname.startsWith("/insights") || pathname.startsWith("/guides") ||
      pathname.includes("-transporter") ||
      pathname.startsWith("/p/") || pathname.startsWith("/track-driver/");
    if (isPublic) document.documentElement.classList.remove("dark");
    else applyTheme(getTheme(user?.id));
  }, [pathname, user?.id]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <TabsProvider>
              <ThemeManager />
              <AppRoutes />
              <Toaster position="bottom-right" />
            </TabsProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}
